import { deepMerge } from 'js/utils/Utils'
import { gsap } from 'gsap'

// import
import DetectionManager from 'js/modules/_base/helper/detection/DetectionManager'
import WindowStateChecker from 'js/modules/_base/events/window/WindowStateChecker'

import Raf from 'js/modules/_base/events/render/Raf'
import Resize from 'js/modules/_base/events/resize/Resize'
import MouseMove from 'js/modules/_base/events/mouse/MouseMove'
import SmoothScroll from 'js/modules/_base/events/scroll/smooth/SmoothScroll'
import IntersectionObserveManager from 'js/modules/_base/events/observer/IntersectionObserveManager'
import ScreenLock from 'js/modules/_base/events/ScreenLock'

import PageCurrentChecker from 'js/modules/_base/navigation/pagaCurrentChekcer/PageCurrentChecker'

import ElementClone from 'js/modules/_base/effects/helper/ElementClone'
import CssHover from 'js/modules/_base/events/mouse/hover/CssHover'

import HeaderColor from 'js/modules/projects/navigation/HeaderColor'
import HeaderManager from 'js/modules/projects/navigation/HeaderManager'
import GNavDropdownController from 'js/modules/projects/navigation/GNavDropdownController'
import GanymedeMenu from 'js/modules/projects/navigation/GanymedeMenu'
import LangSwitcher from 'js/modules/projects/navigation/LangSwitcher'

import TextAlpha1Hover from 'js/modules/projects/events/mouse/hover/TextAlpha1Hover'

// webgl
import Webgl from 'js/webgl/Webgl'

import GridHelper from 'js/helps/GridHelper'

const defaults = {
  debug: false,
  breakPoint: {
    mobile: 767,
  },
  $names: {
    app: '#App',
    header: '[data-header]',
    headerNav: '[data-header-nav]',
  },
  useSmoothScroll: true,
  textureList: {
    bgM: [],
    bgL: [],
    bgXL: [],
    slideshowAItem: [],
    slideshowBItem: [],
    listThumbA: [],
    listThumbB: [],
    bannerA: [],
  },
}

class Common {
  pageVisitedCount = 0
  isStartup = true
  isPageTransitioning = true
  iomEnable = false
  isScrollingFixed = false
  isModalOpening = false

  constructor() {
    deepMerge(this, defaults)

    this.detection = new DetectionManager()
    if (this.detection.isMobileLayout || this.detection.isTabletDevice || this.detection.isMobileDevice) {
      this.detection.useWebgl = false
    }
    this.useMouseHover = !this.detection.isMobileDevice && !this.detection.isTabletDevice

    this.screenLock = new ScreenLock({})
    this.screenLock.lock()

    const $header = document.querySelector(this.$names.header)
    const $headerNav = document.querySelector(this.$names.headerNav)
    this.spMenu = null
    if (this.detection.isMobileLayout) {
      this.spMenu = new GanymedeMenu({})
      if ($headerNav) {
        $headerNav.setAttribute('data-lenis-prevent', '')
      }
    }

    if (this.detection.useWebgl) {
      this.webgl = new Webgl({
        $names: {
          canvas: '[data-gl="common"]',
        },
      })
    }

    // browser window state check
    this.wsChecker = new WindowStateChecker()

    this.raf = new Raf()
    this.iom = new IntersectionObserveManager({
      config: {
        rootMargin: this.detection.isMobileLayout ? '0% 0px 0%' : '0% 0px 0%', // -100~100  px, %
      },
      dataName: {
        observerId: 'observer1Id',
      },
    })
    this.iom2 = new IntersectionObserveManager({
      config: {
        rootMargin: '-1.5% 0px -1.5%',
      },
      dataName: {
        observerId: 'observer2Id',
      },
    })
    this.iom3 = new IntersectionObserveManager({
      config: {
        rootMargin: '-10% 0px -10%',
      },
      dataName: {
        observerId: 'observer3Id',
      },
    })

    this.resize = new Resize()
    this.mouseMove = new MouseMove()

    this.pageCurrentChecker = new PageCurrentChecker({})
    this.headerColor = new HeaderColor({})
    this.headerManager = new HeaderManager({})
    this.gNavDropdownController = new GNavDropdownController({})
    this.langSwitcher = new LangSwitcher({})
    this.gNavDropdownController2 = new GNavDropdownController({
      $names: {
        trigger: '[data-gnav-dropdown-trigger2]',
        target: '[data-gnav-dropdown-target2]',
        parent: '[data-gnav-dropdown-group2]',
      },
      attrNames: {
        trigger: 'data-gnav-dropdown-trigger2',
        target: 'data-gnav-dropdown-target2',
      },
    })

    if ($header) {
      this.headerElementClone = new ElementClone({
        $contentRoot: $header,
      })
      this.headerCssHover = new CssHover({
        $contentRoot: $header,
      })

      if (this.useMouseHover) {
        this.headerTextAlphaHover = new TextAlpha1Hover({
          $contentRoot: $header,
          hoverSelector: 'a, button, [data-hover-alpha1]',
        })
      }
    }
  }

  async init() {
    await new Promise((resolve) => {
      if (this.debug) {
        this._setGui().then(() => {
          this.start()
          resolve()
        })
      } else {
        this.start()
        resolve()
      }
    })
  }

  start() {
    const lang = document.documentElement.lang

    if (lang === 'en') {
      document.documentElement.style.setProperty('--ls-reg', `0.02em`)
      document.documentElement.style.setProperty('--lh-reg', `1.4`)
    }

    this.scroll = new SmoothScroll({
      options: {
        lerp: 0.3,
        // wheelMultiplier: 0.95,
        // lerp: 0.1,
        smoothWheel: this.useSmoothScroll,
      },
    })
    this.resize.init()
    this.raf.init()
    this.mouseMove.init()
    this.scroll.init()
    this.scroll.stop()

    if (this.webgl) this.webgl.init()
    this.spMenu?.init()
    this.pageCurrentChecker?.init()
    this.headerElementClone?.init()
    this.headerCssHover?.init()
    this.headerColor?.init()
    this.headerManager?.init()
    this.gNavDropdownController?.init()
    this.gNavDropdownController2?.init()
    this.langSwitcher?.init()
    this.headerTextAlphaHover?.init()

    // if (this.gui) {
    //   const guiScroll = this.gui.addFolder('Scroll')
    //   guiScroll.add(this.scroll.manager.options, 'lerp', 0.01, 1).step(0.01).name('smooth scroll lerp')
    //   this.gui.close()
    // }
  }

  switchDevMode() {
    if (this.gui.domElement && this.raf.stats && this.raf.stats.dom) {
      this.isDevDomShown = !this.isDevDomShown
      if (this.isDevDomShown) {
        this.gui.domElement.style.display = ''
        this.raf.stats.dom.style.display = ''
      } else {
        this.gui.domElement.style.display = 'none'
        this.raf.stats.dom.style.display = 'none'
      }
    }
  }

  async _setGui() {
    GridHelper.init()
    await import('lil-gui').then((module) => {
      this.gui = new module.GUI({
        width: 400,
      })
      // this.gui.close()
      this.gui.domElement.style.display = 'none'
    })
    this.isDevDomShown = false

    window.addEventListener('keydown', (e) => {
      if (e && e.key && e.key === 'd') {
        this.switchDevMode()
      }
    })
  }
}

export default new Common()